import { currentUser, currentUserOrgId, currentUserPermissions } from "common/selectors/user.selectors";
import { authService } from "common/services/Auth.service";
import { LoaderFunction, redirect } from "react-router";
import { AuthActions } from "common/reducers/auth.actions";
import { UserAsyncActions } from "common/reducers/user.async.actions";
import { authenticated } from "common/selectors/auth.selectors";
import { AuthAsyncActions, ILoginCallbackResult } from "common/reducers/auth.async.actions";
import { store } from "common/store";
import config from "react-global-configuration";
import { notAuthenticated, waitCheckUserAcl, watchRedux } from "src/router/loaders/helper";
import { getRedirectPath } from "common/helpers/Redirect";
import { currentJob, currentJobErrors, currentJobLoading, currentJobName } from "common/selectors/job.selectors";
import {
  contractTitle,
  currentOrg,
  currentOrgName,
  currentOrgLoading,
  estimateTitle,
  estimatorTitle,
  inspectionTitle,
} from "common/selectors/org.selectors";
import { getPrettyName } from "common/helpers/Pretty";
import { getPathname } from "src/router/loaders/common/helpers";
import { OrgActions } from "common/reducers/org.actions";
import { OrgAsyncActions } from "common/reducers/org.async.actions";
import { CommonActions } from "common/reducers/common.actions";
import { CalendarAsyncActions } from "common/reducers/calendar.async.actions";
import { JobActions } from "common/reducers/job.actions";
import { EstimateActions } from "common/reducers/estimate.actions";
import { TokenAsyncActions } from "common/reducers/token.async.actions";
import { authorizedToken } from "common/selectors/token.selectors";
import { trackEventHelper } from "common/helpers/Analytics";

const redirectToParams = (appState: ILoginCallbackResult["appState"]): URLSearchParams => {
  const redirectTo = appState?.redirectTo;

  return new URLSearchParams(redirectTo ? { redirectTo } : {});
};

export const oktaLogoutLoader: LoaderFunction<any> = async () => {
  if (!authService.hasToken()) {
    return redirect("/dabella/login");
  }

  await store.dispatch(AuthAsyncActions.logout());
  return null;
};

export const protectedRedirectLoader: LoaderFunction<any> = async ({ request }) => {
  const url = new URL(request.url);

  if (url.searchParams.has("authUser")) {
    const uid = url.searchParams.get("authUser");
    url.searchParams.delete("authUser");
    if (uid && uid !== "null" && uid !== "undefined") {
      store.dispatch(AuthAsyncActions.changeAccessUid(uid));
    }
    return redirect(url.toString());
  }
  await checkForLocalToken();

  const state = store.getState();
  const isAuthenticated = authenticated(state);
  if (!isAuthenticated) {
    const params = new URLSearchParams();
    const pathname = "/" + getPathname(request);
    const { search } = new URL(request.url);
    params.set("redirectTo", pathname + search);
    // An Okta user can't authenticate through /sso/login.
    const loginRoute = authService.getIdp() === "dabella" ? "/dabella/login" : "/sso/login";
    return redirect(loginRoute + "?" + params.toString());
  }

  const orgId = currentUserOrgId(state);
  if (orgId && isAuthenticated) {
    if (!authorizedToken(state, { orgId: orgId, kind: "occ_pay" })) {
      // Not awaiting this can result in an infinite loop hitting the API under
      // certain error conditions.
      await store.dispatch(TokenAsyncActions.getToken(orgId, "occ_pay"));
    }
  }
  return null;
};

export const rootRedirectLoader: LoaderFunction = async ({ request }) => {
  if (notAuthenticated(request)) return null;
  const hasAccess = await waitCheckUserAcl("flags", "jobs_uiux");
  if (!hasAccess) return redirect(config.get("APP_URL"));
  return null;
};

export const pageTitleUpdaterLoader: LoaderFunction = async ({ request, params }) => {
  if (params?.jobId) {
    setJobPageTitle(request);
  } else if (params?.orgId) {
    setOrgPageTitle(request);
  } else {
    document.title = config.get("APP_NAME_SHORT");
  }
  return null;
};

export const resetJobStateLoader: LoaderFunction = async ({ request, params }) => {
  if (!params.jobId) {
    store.dispatch(JobActions.setCurrentJobId(null));
    store.dispatch(EstimateActions.setCurrentEstimateId(null));
    store.dispatch(CommonActions.setPresentationMode(false));
  }
  return null;
};

export const resetOrgStateLoader: LoaderFunction = async ({ request, params }) => {
  if (!params?.jobId && !params?.orgId) {
    store.dispatch(OrgActions.setCurrentOrgId(null));
    store.dispatch(JobActions.receiveOrgJobParams({}));
  }
  return null;
};

export const setJobPageTitle = async (request: Request) => {
  const title = await getJobPageTitle(request);
  document.title = title;
};

export const setOrgPageTitle = async (request: Request) => {
  try {
    await watchRedux(
      (state) => currentOrg(state) && !currentOrgLoading(state),
      () => {},
    );
  } catch (e) {
    console.error(e);
    document.title = config.get("APP_NAME");
    return;
  }

  const orgName = currentOrgName(store.getState());
  if (orgName) {
    document.title = `${orgName} - ${config.get("APP_NAME_SHORT")}`;
    return;
  }

  document.title = config.get("APP_NAME");
};

export const getJobPageTitle = async (request: Request) => {
  const pathArray = getPathname(request).split("/");

  if (pathArray.includes("screen_shares")) return config.get("APP_NAME_SHORT");

  try {
    await watchRedux((state) => currentJob(state) && !currentJobLoading(state), currentJobErrors);
  } catch (e) {
    console.error(e);
    return config.get("APP_NAME");
  }

  const state = store.getState();
  const page = pathArray[2];
  const jobName = currentJobName(state);
  const estimateName = estimateTitle(state, { plural: true });
  const contractName = contractTitle(state, { plural: true });
  const estimatorName = estimatorTitle(state, { plural: false });
  const inspectionName = inspectionTitle(state, { plural: false });

  if (jobName && page) {
    let pageName;
    switch (page) {
      case "estimates":
        pageName = estimatorName;
        break;
      case "contracts":
        pageName = contractName;
        break;
      case "proposals":
        pageName = estimateName;
        break;
      case "inspection":
        pageName = inspectionName;
        break;

      default:
        pageName = getPrettyName(page);
    }
    const pageTitle = `${jobName}: ${pageName} - ${config.get("APP_NAME_SHORT")}`;
    return pageTitle;
  }

  return config.get("APP_NAME");
};

export const auth0LoginLoader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);

  await checkForLocalToken();

  const isAuthenticated = authenticated(store.getState());
  if (isAuthenticated) {
    const hasAccess = await waitCheckUserAcl("flags", "jobs_uiux");
    if (!hasAccess) {
      return redirect(config.get("APP_URL"));
    }
    return redirect(getRedirectPath(url.searchParams));
  }

  await authService.auth0Login(url.searchParams.get("redirectTo") || undefined);

  return null;
};

export const auth0CallbackLoader: LoaderFunction = async () => {
  const result = await store.dispatch(AuthAsyncActions.auth0LoginCallback());

  if (!result.success) return null;
  return redirect(getRedirectPath(redirectToParams(result.appState)));
};

export const auth0LogoutLoader: LoaderFunction = async () => {
  await store.dispatch(AuthAsyncActions.logout());

  return redirect("/login");
};

export const dabellaLoginLoader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);

  trackEventHelper({
    category: "User",
    action: "redirect login",
    authType: "dabella",
  });
  await authService.dabellaLogin(url.searchParams.get("redirectTo") || undefined);

  return null;
};

export const dabellaCallbackLoader: LoaderFunction = async () => {
  const result = await store.dispatch(AuthAsyncActions.dabellaLoginCallback());
  if (!result.success) return null;

  return redirect(getRedirectPath(redirectToParams(result.appState)));
};

export const uploadLoader: LoaderFunction = async ({ params }) => {
  if (params.key) {
    store.dispatch(OrgAsyncActions.getCustomerOrg(params.key));
  }
  return null;
};

export const termsLoader: LoaderFunction = () => {
  return redirect("https://oneclickcontractor.com/terms-conditions");
};

export const checkForLocalToken = async () => {
  if (authService.hasToken()) {
    const user = currentUser(store.getState());
    store.dispatch(AuthActions.setAuthenticated(true));
    store.dispatch(
      AuthActions.setTokens({ token: authService.getToken(), refreshToken: authService.getRefreshToken() }),
    );

    if (authService.getIdp() === "occ") {
      authService.setupOccManualTokenRefresh();
    }

    if (!user) {
      const email = authService.getCurrentUser().email;
      await store.dispatch(UserAsyncActions.getCurrentUserByEmail(email));
    }
  } else {
    store.dispatch(AuthActions.setAuthenticated(false));
  }
};

export const calendarOptionsLoader: LoaderFunction = async ({ request }) => {
  if (notAuthenticated(request)) return null;
  store.dispatch(CalendarAsyncActions.listCalendars());
  store.dispatch(CalendarAsyncActions.getCronofyUrl());

  return null;
};

export const financeTabLoader: LoaderFunction = async ({ request }) => {
  if (notAuthenticated(request)) return null;
  const state = store.getState();
  const permissions = currentUserPermissions(state);
  const canPaymentReport = permissions?.get("payment")?.includes("payment_report");
  const canDepositReport = permissions?.get("payment")?.includes("deposit_report");
  const canChargebackReport = permissions?.get("payment")?.includes("chargeback_report");
  const defaultActiveTab =
    canChargebackReport && !canPaymentReport && !canDepositReport
      ? "chargebacks"
      : canDepositReport && !canPaymentReport
        ? "deposits"
        : "payments";

  return redirect(defaultActiveTab);
};
